import { useEffect, useState } from 'react';

import { logout } from '@/shared/api';

import { hasSessionEnded } from './auth';

/**
 * Ends the session once the access token expires, checked on mount and whenever the tab
 * becomes visible again. An expired token used in between is rejected with a 401, which
 * the HTTP clients turn into the same logout.
 * Returns false once the session is over, while the login redirect is on its way.
 */
export const useSessionExpiry = (): boolean => {
  const [isActive, setIsActive] = useState(() => !hasSessionEnded());

  useEffect(() => {
    const check = (trigger: 'startup' | 'tab_visible') => {
      if (hasSessionEnded()) {
        setIsActive(false);
        void logout({ reason: 'session_expired', context: { trigger } });
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check('tab_visible');
      }
    };

    check('startup');
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return isActive;
};
